// Role hierarchy: platform_admin(6) > super_admin(5) > admin(4) > manager(3) > agent(2) > viewer(1)
const ROLE_HIERARCHY = {
  platform_admin: 6,
  super_admin: 5,
  admin: 4,
  manager: 3,
  agent: 2,
  viewer: 1,
};

// platform_admin (the cross-tenant superuser) is listed explicitly on every
// non-platform-only permission so UI gates don't lock them out. Mirror of
// server/src/config/permissions.js — keep these two files in sync.
const PERMISSIONS = {
  'dashboard.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent', 'viewer'],
  'business_api.config': ['platform_admin', 'super_admin', 'admin'],
  'business_api.guide': ['platform_admin', 'super_admin', 'admin'],
  'business_api.send': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'business_api.inbox': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'business_api.campaigns': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'business_api.templates': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'business_api.catalog': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'business_api.phone_mgmt': ['platform_admin', 'super_admin', 'admin'],
  'business_api.analytics': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'business_api.media': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'business_api.flows': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'business_api.meta_analytics': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'business_api.calls': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'business_api.mm_lite': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'messages.send': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'messages.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent', 'viewer'],
  'campaigns.manage': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'campaigns.view': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'subscriptions.manage': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'subscriptions.view': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'templates.manage': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'templates.view': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'wa.setup': ['platform_admin', 'super_admin', 'admin'],
  'wa.send': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.groups': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.status': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.chatbot': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.tools': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.templates': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'wa.contacts': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.online': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.status_reply.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'wa.status_reply.manage': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'contacts.manage': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'contacts.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'reports.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'viewer'],
  'activity_logs.view': ['platform_admin', 'super_admin', 'admin'],
  'api_settings.manage': ['platform_admin', 'super_admin'],
  'webhooks.manage': ['platform_admin', 'super_admin'],
  'webhook_tester.manage': ['platform_admin', 'super_admin'],
  'api_explorer.view': ['platform_admin', 'super_admin'],
  'integration_guide.view': ['platform_admin', 'super_admin'],
  'profile.manage': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent', 'viewer'],
  'billing.manage': ['platform_admin', 'super_admin'],
  'billing.view': ['platform_admin', 'super_admin', 'admin'],
  'whitelabel.manage': ['platform_admin', 'super_admin'],
  'whitelabel.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent', 'viewer'],
  'users.manage': ['platform_admin', 'super_admin', 'admin'],

  // Chat Widget
  'chat_widget.manage': ['platform_admin', 'super_admin', 'admin'],
  'chat_widget.view': ['platform_admin', 'super_admin', 'admin', 'manager'],

  // Canned Responses
  'canned_responses.manage': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'canned_responses.use': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],

  // Inbox AI assist
  'inbox.ai_assist': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],

  // CSAT
  'csat.view': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'csat.manage': ['platform_admin', 'super_admin', 'admin'],

  // SLA
  'sla.view': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'sla.manage': ['platform_admin', 'super_admin', 'admin'],

  // Privacy / DPDP
  'privacy.export': ['platform_admin', 'super_admin', 'admin'],
  'privacy.erase': ['platform_admin', 'super_admin', 'admin'],

  // Status page admin
  'status.manage': ['platform_admin'],

  // Bookings
  'bookings.manage': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'bookings.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],

  // Payments
  'payments.manage': ['platform_admin', 'super_admin', 'admin'],
  'payments.create': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],
  'payments.view': ['platform_admin', 'super_admin', 'admin', 'manager', 'agent'],

  // Click-to-WhatsApp ads
  'ctwa_ads.manage': ['platform_admin', 'super_admin', 'admin'],
  'ctwa_ads.view': ['platform_admin', 'super_admin', 'admin', 'manager'],

  // Proxy IPs — platform-level infrastructure; restricted to platform staff.
  'proxy_ips.view': ['platform_admin'],
  'proxy_ips.manage': ['platform_admin'],

  // WhatsApp Number Health Score
  'health_score.view': ['platform_admin', 'super_admin', 'admin', 'manager'],

  // Industry packs
  'industry_packs.view': ['platform_admin', 'super_admin', 'admin', 'manager'],
  'industry_packs.apply': ['platform_admin', 'super_admin', 'admin'],
};

export const hasPermission = (role, permission) => {
  const allowedRoles = PERMISSIONS[permission];
  if (!allowedRoles) return false;
  return allowedRoles.includes(role);
};

export const getRoleLevel = (role) => {
  return ROLE_HIERARCHY[role] || 0;
};

export const isRoleHigherOrEqual = (role, targetRole) => {
  return getRoleLevel(role) >= getRoleLevel(targetRole);
};

export { ROLE_HIERARCHY, PERMISSIONS };
